import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { apiBase, authHeaders, hasOrgContext } from '../utils/authHeaders';
import { apiErrorMessage } from '../utils/apiError';

/**
 * Floating in-app feedback button (continuous-feedback loop, feedback.submitted).
 *
 * Captures the current route as `screen` and, when a screen has published one
 * via setFeedbackRecord(), the selected record id. Text is stored only in the
 * tenant (the emitted event carries length + themes) — see backend/feedback/api.py.
 */

export const MAX_TEXT = 1000;
let currentRecordRef = null;

/** Screens call this when the user selects a record (null to clear). */
export function setFeedbackRecord(ref) {
  currentRecordRef = ref ? String(ref).slice(0, 120) : null;
  window.dispatchEvent(new CustomEvent('feedback:record', { detail: currentRecordRef }));
}

const SAFE_REF = /^[A-Za-z0-9_.:/-]+$/;

export function screenFromPath(pathname) {
  const clean = (pathname || '/').replace(/[^A-Za-z0-9_./:-]/g, '').slice(0, 120);
  return clean.startsWith('/') ? clean : `/${clean}`;
}

export default function FeedbackButton() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(null);
  const [text, setText] = useState('');
  const [recordRef, setRecordRef] = useState(currentRecordRef);
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [error, setError] = useState('');

  useEffect(() => {
    const onRecord = (e) => setRecordRef(e.detail || null);
    window.addEventListener('feedback:record', onRecord);
    return () => window.removeEventListener('feedback:record', onRecord);
  }, []);

  if (!hasOrgContext()) return null;

  const screen = screenFromPath(location.pathname);
  const canSend = status !== 'sending' && (rating !== null || text.trim().length > 0);

  const reset = () => { setRating(null); setText(''); setError(''); setStatus('idle'); };

  const submit = async (e) => {
    e.preventDefault();
    if (!canSend) return;
    setStatus('sending');
    setError('');
    const body = { screen, text: text.trim() };
    if (rating !== null) body.rating = rating;
    if (recordRef && SAFE_REF.test(recordRef)) body.record_ref = recordRef;
    try {
      const res = await fetch(`${apiBase()}/api/feedback`, {
        method: 'POST',
        headers: authHeaders({ json: true }),
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(apiErrorMessage(data.detail, `Could not send feedback (${res.status})`));
      }
      setStatus('sent');
      setRating(null);
      setText('');
    } catch (err) {
      setStatus('error');
      setError(err.message || 'Could not send feedback');
    }
  };

  return (
    <div className={['/process-discovery', '/workforce'].includes(location.pathname)
      ? 'mx-auto flex max-w-5xl justify-end px-4 pb-6 sm:px-6'
      : 'fixed bottom-4 right-4 z-50'} data-testid="feedback-widget">
      {!open && (
        <button
          type="button"
          onClick={() => { reset(); setOpen(true); }}
          className="px-4 py-2 rounded-full shadow-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600"
          data-testid="feedback-open"
        >
          Feedback
        </button>
      )}
      {open && (
        <form
          onSubmit={submit}
          aria-label="Send feedback"
          className="w-80 max-w-[calc(100vw-2rem)] bg-white border border-gray-200 rounded-xl shadow-xl p-4"
        >
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-sm font-semibold text-gray-900">Feedback on this screen</h2>
            <button type="button" onClick={() => setOpen(false)} aria-label="Close feedback"
              className="text-gray-500 hover:text-gray-800" data-testid="feedback-close">×</button>
          </div>
          <p className="text-xs text-gray-500 mb-2" data-testid="feedback-context">
            {screen}{recordRef ? ` · ${recordRef}` : ''}
          </p>
          {status === 'sent' ? (
            <p className="text-sm text-green-700" role="status" data-testid="feedback-thanks">Thanks — sent.</p>
          ) : (
            <>
              <div className="flex gap-1 mb-2" role="group" aria-label="Rating">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" aria-pressed={rating === n} aria-label={`Rate ${n}`}
                    onClick={() => setRating(rating === n ? null : n)}
                    className={`w-8 h-8 rounded border text-sm ${rating === n ? 'bg-primary-500 text-white' : 'bg-white'}`}>
                    {n}
                  </button>
                ))}
              </div>
              <textarea
                aria-label="Feedback text"
                value={text}
                maxLength={MAX_TEXT}
                onChange={(e) => setText(e.target.value.slice(0, MAX_TEXT))}
                rows={3}
                placeholder="What worked, what didn't? (no customer details please)"
                className="w-full border rounded-lg px-2 py-1 text-sm"
              />
              <div className="flex justify-between items-center mt-2">
                <span className="text-xs text-gray-400">{text.length}/{MAX_TEXT}</span>
                <button type="submit" disabled={!canSend}
                  className="px-3 py-1 rounded-lg bg-primary-500 text-white text-sm disabled:opacity-50"
                  data-testid="feedback-submit">
                  {status === 'sending' ? 'Sending…' : 'Send'}
                </button>
              </div>
              {error && <p className="text-xs text-red-600 mt-2" role="alert">{error}</p>}
            </>
          )}
        </form>
      )}
    </div>
  );
}
